/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useMemo, useReducer, type ReactNode } from 'react';

import { buildQuestionPlan } from '@/utils/questionPlan';
import { topics } from '@/data/topics';
import type { Question } from '@/models/question';
import type { PositionAnswerValue, PositionsFlowState, PositionsMode } from '@/models/position';

type PositionsFlowAction =
  | { type: 'selectMode'; mode: Exclude<PositionsMode, null> }
  | { type: 'setTopics'; topicIds: string[] }
  | { type: 'recordAnswer'; questionId: string; answer: PositionAnswerValue }
  | { type: 'skipQuestion'; questionId: string }
  | { type: 'advanceQuestion'; totalQuestions: number }
  | { type: 'goBackQuestion' }
  | { type: 'setQuestionIndex'; index: number }
  | { type: 'setPhase'; phase: 'setup' | 'topics' | 'questions' | 'processing' | 'summary' }
  | { type: 'reset' };

type PositionsFlowContextValue = {
  state: PositionsFlowState;
  answers: PositionsFlowState['answers'];
  selectedTopics: typeof topics;
  questionPlan: Question[];
  totalQuestions: number;
  currentQuestion: Question | null;
  selectMode: (mode: Exclude<PositionsMode, null>) => void;
  setSelectedTopics: (topicIds: string[]) => void;
  toggleTopic: (topicId: string) => void;
  recordAnswer: (questionId: string, answer: PositionAnswerValue) => void;
  skipQuestion: (questionId: string, totalQuestions: number) => void;
  advanceQuestion: (totalQuestions: number) => void;
  goBackQuestion: () => void;
  setQuestionIndex: (index: number) => void;
  setPhase: (phase: 'setup' | 'topics' | 'questions' | 'processing' | 'summary') => void;
  restartFlow: () => void;
};

const initialState: PositionsFlowState = {
  mode: null,
  selectedTopicIds: [],
  answers: {},
  currentQuestionIndex: 0,
  phase: 'setup',
};

function loadState(): PositionsFlowState {
  return initialState;
}

function normalizeTopicIds(topicIds: string[]): string[] {
  const seen = new Set<string>();

  return topicIds.filter((topicId) => {
    if (seen.has(topicId)) {
      return false;
    }

    const exists = topics.some((topic) => topic.id === topicId);

    if (!exists) {
      return false;
    }

    seen.add(topicId);
    return true;
  });
}

function reducer(state: PositionsFlowState, action: PositionsFlowAction): PositionsFlowState {
  switch (action.type) {
    case 'selectMode':
      return {
        mode: action.mode,
        selectedTopicIds: [],
        answers: {},
        currentQuestionIndex: 0,
        phase: 'topics',
      };
    case 'setTopics':
      return {
        ...state,
        selectedTopicIds: normalizeTopicIds(action.topicIds).slice(0, 3),
        answers: {},
        currentQuestionIndex: 0,
        phase: 'topics',
      };
    case 'recordAnswer':
      return {
        ...state,
        answers: {
          ...state.answers,
          [action.questionId]: action.answer,
        },
      };
    case 'skipQuestion': {
      return {
        ...state,
        answers: {
          ...state.answers,
          [action.questionId]: 'skip',
        },
      };
    }
    case 'advanceQuestion':
      return {
        ...state,
        currentQuestionIndex: Math.min(state.currentQuestionIndex + 1, action.totalQuestions),
        phase:
          state.currentQuestionIndex + 1 >= action.totalQuestions ? 'processing' : 'questions',
      };
    case 'goBackQuestion':
      return {
        ...state,
        currentQuestionIndex: Math.max(0, state.currentQuestionIndex - 1),
        phase: state.currentQuestionIndex <= 1 ? 'topics' : 'questions',
      };
    case 'setQuestionIndex':
      return {
        ...state,
        currentQuestionIndex: Math.max(0, action.index),
        phase: 'questions',
      };
    case 'setPhase':
      return {
        ...state,
        phase: action.phase,
      };
    case 'reset':
      return initialState;
    default:
      return state;
  }
}

const PositionsFlowContext = createContext<PositionsFlowContextValue | undefined>(undefined);

export function PositionsFlowProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, dispatch] = useReducer(reducer, undefined, loadState);

  const questionPlan = useMemo(() => buildQuestionPlan(state.mode, state.selectedTopicIds), [
    state.mode,
    state.selectedTopicIds,
  ]);

  const totalQuestions = questionPlan.length;
  const currentQuestion = questionPlan[state.currentQuestionIndex] ?? null;

  const value = useMemo<PositionsFlowContextValue>(
    () => ({
      state,
      answers: state.answers,
      selectedTopics: state.selectedTopicIds
        .map((topicId) => topics.find((topic) => topic.id === topicId))
        .filter((topic): topic is (typeof topics)[number] => Boolean(topic)),
      questionPlan,
      totalQuestions,
      currentQuestion,
      selectMode: (mode) => dispatch({ type: 'selectMode', mode }),
      setSelectedTopics: (topicIds) => dispatch({ type: 'setTopics', topicIds }),
      toggleTopic: (topicId) => {
        const hasTopic = state.selectedTopicIds.includes(topicId);

        if (hasTopic) {
          dispatch({
            type: 'setTopics',
            topicIds: state.selectedTopicIds.filter((entry) => entry !== topicId),
          });
          return;
        }

        if (state.selectedTopicIds.length >= 3) {
          return;
        }

        dispatch({ type: 'setTopics', topicIds: [...state.selectedTopicIds, topicId] });
      },
      recordAnswer: (questionId, answer) =>
        dispatch({ type: 'recordAnswer', questionId, answer }),
      skipQuestion: (questionId, total) => {
        dispatch({ type: 'skipQuestion', questionId });
        dispatch({ type: 'advanceQuestion', totalQuestions: total });
      },
      advanceQuestion: (total) => dispatch({ type: 'advanceQuestion', totalQuestions: total }),
      goBackQuestion: () => dispatch({ type: 'goBackQuestion' }),
      setQuestionIndex: (index) => dispatch({ type: 'setQuestionIndex', index }),
      setPhase: (phase) => dispatch({ type: 'setPhase', phase }),
      restartFlow: () => {
        dispatch({ type: 'reset' });
      },
    }),
    [currentQuestion, questionPlan, state, totalQuestions],
  );

  return <PositionsFlowContext.Provider value={value}>{children}</PositionsFlowContext.Provider>;
}

export function usePositionsFlow(): PositionsFlowContextValue {
  const context = useContext(PositionsFlowContext);

  if (!context) {
    throw new Error('usePositionsFlow deve ser usado dentro de PositionsFlowProvider.');
  }

  return context;
}
